// Drop an event when the rejected payload never got an HTTP response, keep it at every
// real backend status. Status 0 means dropped connectivity, a CORS preflight failure, a
// TLS failure, a suspended tab, or a navigation cancelling an in-flight XHR; a non-zero
// status is a refusal engineering can act on. See docs/SENTRY_FILTERING.md for why this
// reads hint.originalException rather than event.extra, and for the SDK contracts below.

export function isDroppableStatusZero(hint) {
  if (!hint || typeof hint !== 'object') return false

  const orig = hint.originalException
  if (!orig || typeof orig !== 'object') return false
  if (!('status' in orig)) return false

  // Number(null), Number(''), Number(false) and Number([]) all equal 0, so a malformed
  // payload would otherwise be dropped as if it were a connectivity failure.
  const raw = orig.status
  if (typeof raw !== 'number' && typeof raw !== 'string') return false
  if (typeof raw === 'string' && raw.trim() === '') return false

  // Coerced, not ===: pusher renders status as the string '0' on one capture path and as
  // a number on the other.
  return Number(raw) === 0
}

// Returning undefined does NOT drop: the SDK logs a warning and sends the event anyway,
// so every branch returns explicitly. A throw here would be re-captured as a second
// event, so the catch defaults to keep; a predicate bug then costs noise, not blindness.
export function beforeSend(event, hint) {
  try {
    if (isDroppableStatusZero(hint)) return null
    return event
  } catch (e) {
    return event
  }
}

// Applied by InboundFilters inside prepareEvent, upstream of beforeSend. A string entry is an
// indexOf substring match, a RegExp entry goes through .test(), and both run against
// [value, type + ': ' + value].
//
// The navigation entry is a prefix regex rather than the two exact strings seen in Sentry:
// vue-router interpolates from.fullPath and to.fullPath, which carry query and hash, so exact
// literals would cover 2 route pairs out of roughly 40 and no query-string variants. That
// matters because these entries are the fallback for the router wrapper, and a fallback that
// only fires on two paths is not a fallback. The message is built in exactly one place in
// vue-router and is definitionally a superseded navigation, so the wider match cannot bury a
// real defect. No escaping hazard either: the prefix carries no regex metacharacters.
export const ignoreErrors = [
  'ResizeObserver loop',
  /^Navigation cancelled from /,
  'NavigationDuplicated'
]
